import React from 'react';
import { AlertTriangle, Check, X, ShieldAlert, Loader2 } from 'lucide-react';
import { SettingFieldMeta } from '@/types/api';
import { formatSettingLabel } from './settingsUtils';

export interface SettingDiffItem {
  key: string;
  meta: SettingFieldMeta;
  oldValue: boolean | number | string;
  newValue: boolean | number | string;
}

interface SettingsConfirmModalProps {
  isOpen: boolean;
  diffs: SettingDiffItem[];
  reason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export const SettingsConfirmModal: React.FC<SettingsConfirmModalProps> = ({
  isOpen,
  diffs,
  reason,
  onReasonChange,
  onConfirm,
  onCancel,
  isSaving,
}) => {
  if (!isOpen || diffs.length === 0) return null;

  const hasPositionImpact = diffs.some((d) => d.meta.affects_existing_positions);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={() => !isSaving && onCancel()}
      />

      {/* Modal Dialog */}
      <div
        className="relative z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl shadow-2xl p-4 sm:p-6 space-y-4 sm:space-y-5 animate-fade-in-up font-mono my-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1 pr-2">
            <h2 id="confirm-modal-title" className="text-sm sm:text-base font-bold text-[var(--text-primary)] flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 shrink-0" />
              <span>Review Runtime Setting Modifications</span>
            </h2>
            <p className="text-[11px] sm:text-xs text-[var(--text-muted)]">
              The following {diffs.length} parameter{diffs.length > 1 ? 's' : ''} will be patched directly into the active Oracle engine.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-50 shrink-0 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning Alert if affects existing positions */}
        {hasPositionImpact && (
          <div className="p-3 rounded-lg bg-amber-950/40 border border-amber-800/60 text-amber-300 text-xs flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Affects Active Positions</p>
              <p className="text-[11px] text-amber-300/90 mt-0.5">
                One or more modified settings will alter evaluation thresholds for currently held open positions in the next cycle.
              </p>
            </div>
          </div>
        )}

        {/* Diff Table */}
        <div className="border border-[var(--border-default)] rounded-lg overflow-x-auto max-h-52 sm:max-h-60 overflow-y-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-[var(--bg-surface-elevated)]/90 text-[var(--text-muted)] border-b border-[var(--border-default)] text-[10px] sm:text-[11px] uppercase">
                <th className="p-2 sm:p-2.5">Parameter</th>
                <th className="p-2 sm:p-2.5">Current Value</th>
                <th className="p-2 sm:p-2.5">New Value</th>
                <th className="p-2 sm:p-2.5 text-right">Scope</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {diffs.map(({ key, meta, oldValue, newValue }) => (
                <tr key={key} className="hover:bg-[var(--bg-surface-elevated)]/40">
                  <td className="p-2 sm:p-2.5">
                    <div className="font-semibold text-[var(--text-primary)] text-[11px] sm:text-xs">{formatSettingLabel(key, meta)}</div>
                    <div className="text-[9px] sm:text-[10px] text-[var(--text-muted)]">{key}</div>
                  </td>
                  <td className="p-2 sm:p-2.5 text-[var(--text-muted)] line-through text-[11px] sm:text-xs whitespace-nowrap">
                    {typeof oldValue === 'boolean'
                      ? (oldValue ? 'ENABLED' : 'DISABLED')
                      : `${oldValue} ${meta.unit !== 'count' && meta.unit !== 'boolean' ? meta.unit : ''}`}
                  </td>
                  <td className="p-2 sm:p-2.5 font-bold text-emerald-400 text-[11px] sm:text-xs whitespace-nowrap">
                    {typeof newValue === 'boolean'
                      ? (newValue ? 'ENABLED' : 'DISABLED')
                      : `${newValue} ${meta.unit !== 'count' && meta.unit !== 'boolean' ? meta.unit : ''}`}
                  </td>
                  <td className="p-2 sm:p-2.5 text-right whitespace-nowrap">
                    {meta.affects_existing_positions ? (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded bg-amber-950/70 text-amber-400 border border-amber-800/60 font-semibold">
                        Existing Pos
                      </span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] sm:text-[10px] rounded bg-[var(--bg-surface-elevated)] text-[var(--text-muted)] border border-[var(--border-default)]">
                        New Only
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Audit Reason Input */}
        <div className="space-y-1.5">
          <label htmlFor="audit-reason" className="block text-xs font-semibold text-[var(--text-secondary)]">
            Audit Reason (Optional)
          </label>
          <input
            id="audit-reason"
            type="text"
            disabled={isSaving}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g. Risk adjustment for high market volatility"
            className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-default)] rounded-md text-xs text-[var(--text-primary)] placeholder-[var(--text-dim)] focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 pt-3 border-t border-[var(--border-default)]">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="w-full sm:w-auto px-4 py-2 rounded-md border border-[var(--border-strong)] bg-[var(--bg-surface-elevated)] hover:bg-[var(--bg-surface-muted)] text-[var(--text-secondary)] text-xs font-semibold transition-all disabled:opacity-50 text-center"
          >
            Cancel & Keep Editing
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white text-xs font-semibold shadow-lg shadow-emerald-950/50 transition-all disabled:opacity-50 active:scale-[0.98]"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Applying Changes...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Confirm & Apply</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
